import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { CATEGORIES, DESKS, TECH_QUICK_LINKS, deskForCategory, quickLinkHref, typeById } from '../demo/catalog.js';
import { useDemo } from '../demo/DemoContext.jsx';
import { loadTickets, titleFor } from '../demo/store.js';
import { Chip, Pri, ServerNotice, SourceChip, StatusChip } from '../components/Chips.jsx';
import { isOpen, slaFor, worstSla } from '../lib/ticketView.js';
import { shortWhen } from '../lib/tone.js';

/* The requester home.
 *
 * The mockup led with a free-text box. That box is GONE — Grant, 2026-08-14:
 * "I'm not letting them free form anything." Everything else about the layout
 * survives: the date, the two-line hero, the card floating over the hero wash,
 * the quick starts, and the open-requests / request-something pair. What sat in
 * the box is now the five busiest categories as one-tap starts.
 *
 * TWO LAYOUTS since 2026-08-21. A field technician gets the thumb-sized one —
 * six fat buttons and their open requests, nothing else. Everyone else gets the
 * desk layout, unchanged. Grant, 2026-08-21: techs need "pretty simple clicks."
 *
 * Which one you get is `isField`, an HR fact, NOT an access check. Nobody is
 * locked out of anything either way — both layouts reach the same wizard, and
 * the tech layout's last button opens the full category list.
 */

const TILES = CATEGORIES.slice(0, 4);

export default function Home() {
  const { me, isIT, isAdmin, isField, walkthrough, myDesks, reference, version } = useDemo();
  const navigate = useNavigate();
  const [mine, setMine] = useState(null);
  const [all, setAll] = useState(null);
  const [serverState, setServerState] = useState('ok');

  useEffect(() => {
    let cancelled = false;
    Promise.all([loadTickets({ mine: true, email: me.email }), loadTickets()]).then(([m, a]) => {
      if (cancelled) return;
      setMine(m.rows);
      setAll(a.rows);
      setServerState(m.serverState !== 'ok' ? m.serverState : a.serverState);
    });
    return () => {
      cancelled = true;
    };
  }, [me.email, version]);

  // Normal builds count real tickets only: a browser-only demo ticket is not
  // workload anyone has been asked to handle. loadTickets no longer returns demo
  // rows outside a walkthrough (S08 part B); this filter stays as a second guard.
  const myOpen = (mine || []).filter((t) => walkthrough || t.fromServer === true).filter(isOpen);
  // Normal Home counts real workload only (same second guard as above).
  const queueOpen = (all || [])
    .filter((t) => walkthrough || t.fromServer === true)
    .filter(isOpen)
    .filter((t) => (isAdmin || myDesks.includes(deskForCategory(t.categoryId)?.id)));
  const unassigned = queueOpen.filter((t) => !t.assignee);
  const breached = queueOpen.filter((t) => {
    const w = worstSla(slaFor(t, reference));
    return w && w.state === 'breached';
  });

  /* ---------------------------------------------------- the technician layout
   *
   * One column, thumb-first, no prose. A tech reading this is standing at a job
   * site with one hand free, so everything the desk layout offers to browse
   * with — the volume tiles, the queue stats, the "ten areas in total" note — is
   * deliberately absent. Fewer doors, bigger doors.
   *
   * The buttons are `.tap`, NOT `.thumb`. That name is already taken by the
   * attachment previews on intake and the ticket thread — see the note in
   * index.css for what using it here actually broke.
   */
  if (isField) {
    return (
      <>
        <section className="hero hero--tight">
          <h1 className="hero__title">
            Hi {me.firstName}.
            <br />
            <strong>What broke?</strong>
          </h1>
          <p className="hero__sub hero__sub--short">Tap the closest one. Two taps and IT has it.</p>
        </section>

        <div className="taps">
          {TECH_QUICK_LINKS.map((link) => (
            <button
              key={link.typeId || 'other'}
              type="button"
              className={`tap${link.typeId ? '' : ' tap--last'}`}
              onClick={() => navigate(quickLinkHref(link))}
            >
              <span className="material-symbols-outlined" aria-hidden="true">{link.icon}</span>
              <b>{link.label}</b>
              <small>{link.hint}</small>
            </button>
          ))}
        </div>

        <section className="section">
          <OpenRequests mine={mine} myOpen={myOpen} serverState={serverState} />
        </section>
      </>
    );
  }

  /* ------------------------------------------------------------ the desk layout */
  return (
    <>
      <section className="hero">
        <p className="hero__eyebrow">
          {new Date().toLocaleDateString(undefined, { weekday: 'long', day: 'numeric', month: 'long' })}
        </p>
        <h1 className="hero__title">
          Hi {me.firstName}.
          <br />
          <strong>What do you need help with?</strong>
        </h1>
        <p className="hero__sub">
          Pick a desk below and answer a few short questions. You'll get an email whenever
          someone replies, and you answer right here.
        </p>
      </section>

      {/* The three tiles. Grant, 2026-09-14: "Three tiles when they first enter
          the portal. IT Help, Service Titan Help, AI Access."

          Rendered from DESKS rather than written out three times, so adding a
          fourth desk is a catalog.js edit and a renamed one fails a test
          instead of becoming a dead tile. Same reasoning as TECH_QUICK_LINKS.

          The hero no longer says "What can IT help with?" — two of the three
          desks are not IT. */}
      <div className="desks">
        {DESKS.map((desk) => (
          <button
            key={desk.id}
            type="button"
            className="desk"
            onClick={() => navigate(`/new?desk=${desk.id}`)}
          >
            <span className="material-symbols-outlined" aria-hidden="true">{desk.icon}</span>
            <b>{desk.label}</b>
            <small>{desk.blurb}</small>
            <span className="desk__who">{desk.hint}</span>
          </button>
        ))}
      </div>

      {isIT && all ? (
        <section className="section">
          <div className="section__head">
            <h2 className="section__title">The queue right now</h2>
            <Link to="/queue" className="section__link">Open the queue →</Link>
          </div>
          <div className="grid">
            <Stat n={queueOpen.length} label="Open tickets" />
            <Stat n={unassigned.length} label="Nobody on it" tone={unassigned.length ? 'warn' : undefined} />
            <Stat n={breached.length} label="Past their target" tone={breached.length ? 'error' : undefined} />
          </div>
        </section>
      ) : null}

      <section className="section grid--home">
        <OpenRequests mine={mine} myOpen={myOpen} serverState={serverState} />

        <div>
          <div className="section__head">
            <h2 className="section__title">Straight to it</h2>
            <Link to="/new" className="section__link">Browse all →</Link>
          </div>
          <div className="card">
            {/* The busiest four, for people who already know what they want and
                do not need the desk step. They still carry their desk in the
                URL so intake opens in the right place. */}
            <div className="grid" style={{ gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              {TILES.map((c) => (
                <button
                  key={c.id}
                  type="button"
                  className="tile"
                  onClick={() => navigate(`/new?desk=${c.deskId}&category=${c.id}`)}
                >
                  <span className="material-symbols-outlined" aria-hidden="true">{c.icon}</span>
                  <b>{c.label}</b>
                  <small>{c.blurb}</small>
                </button>
              ))}
            </div>
            <p className="muted" style={{ marginTop: 12, fontSize: 11.5 }}>
              {CATEGORIES.length} areas across the three desks —{' '}
              <Link to="/new">see the rest</Link>. Nothing fits? Pick the closest; it can be moved.
            </p>
          </div>
        </div>
      </section>

      {/* Knowledge base and system status were on the mockup and are deliberately
          NOT here — both are out of scope per the spec. The KB surface waits for
          the AI summaries to populate it. */}
      <p className="muted" style={{ marginTop: 22, fontSize: 11.5 }}>
        {walkthrough ? <><Chip tone="flat" icon="science">Prototype</Chip>{' '}</> : null}
        Common fixes and a system-status panel were in the design but are out of scope for now —
        the knowledge base waits until resolved tickets have filled it.
      </p>
    </>
  );
}

/* Shared by both layouts. Extracted when the technician layout landed rather
 * than copied, so a change to how a ticket row reads happens in one place. The
 * desk layout puts it in the left column of `grid--home`; the tech layout stacks
 * it under the buttons. Same markup either way. */
function OpenRequests({ mine, myOpen, serverState }) {
  return (
    <div>
      <div className="section__head">
        <h2 className="section__title">My open requests</h2>
        <span className="section__note">
          {myOpen.length ? `${myOpen.length} open` : 'nothing open'}
        </span>
        <Link to="/mine" className="section__link">View all →</Link>
      </div>

      <ServerNotice state={serverState} />

      {mine === null ? (
        <div className="card"><p className="muted">Loading…</p></div>
      ) : myOpen.length === 0 ? (
        <div className="empty">
          <p className="empty__title">Nothing open</p>
          <p className="empty__body">
            Anything you raise shows up here with its status and the whole conversation.
          </p>
        </div>
      ) : (
        <div className="card card--tight">
          {myOpen.slice(0, 4).map((t) => {
            const type = typeById(t.requestTypeId);
            return (
              <Link key={t.number} to={`/ticket/${t.number}`} className="tk">
                <span className="tk__icon">
                  <span className="material-symbols-outlined" aria-hidden="true">
                    {type?.icon || 'confirmation_number'}
                  </span>
                </span>
                <span className="tk__main">
                  <b>{titleFor(t)}</b>
                  <small>
                    <span className="ref">{t.number}</span> <SourceChip ticket={t} /> · raised {shortWhen(t.raisedAt)}
                  </small>
                </span>
                <span className="tk__end">
                  <StatusChip ticket={t} />
                  <small>
                    <Pri priority={t.priority} ruleName={t.priorityRuleName} />
                  </small>
                </span>
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}

function Stat({ n, label, tone }) {
  const colour =
    tone === 'error' ? 'var(--ihs-red)' : tone === 'warn' ? 'var(--ihs-warn-ink)' : 'var(--ihs-anchor-blue)';
  return (
    <div className="card">
      <div style={{ font: '300 38px/1 Roboto, sans-serif', color: colour }}>{n}</div>
      <div style={{ marginTop: 8, font: '12.5px Roboto, sans-serif', color: 'var(--ihs-ink-3)' }}>{label}</div>
    </div>
  );
}
